import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import ReminderService from '../../services/reminderService';
import { 
  Box, 
  Button, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Chip, 
  IconButton, 
  Divider,
  Fab,
  Tooltip
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Event as EventIcon,
  Repeat as RepeatIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Import our custom components
import PageHeader from '../layout/PageHeader';
import EmptyState from '../common/EmptyState';
import FormDialog from '../common/FormDialog';
import ReminderForm from '../common/ReminderForm';
import ConfirmDialog from '../common/ConfirmDialog';
import Loading from '../layout/Loading';

const Reminders = () => {
  const { token } = useContext(AuthContext);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduledDate: new Date().toISOString().slice(0, 16),
    recurrence: 'none',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, [token]);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      
      const data = await ReminderService.getAllReminders(token);
      
      if (data.success) {
        // Also fetch associated notification status for each reminder
        const remindersWithStatus = await Promise.all(data.reminders.map(async (reminder) => {
          try {
            const notificationData = await ReminderService.getNotifications(reminder._id, token);
            
            // Add notification status to the reminder object
            if (notificationData.success && notificationData.notifications && notificationData.notifications.length > 0) {
              // Get the latest notification
              const latestNotification = notificationData.notifications[0];
              return {
                ...reminder,
                notificationStatus: latestNotification.status,
                hasNotificationSent: latestNotification.status === 'sent'
              };
            }
            
            return reminder;
          } catch (notifError) {
            console.error(`Error fetching notifications for reminder ${reminder._id}:`, notifError);
            return reminder;
          }
        }));
        
        setReminders(remindersWithStatus);
      } else {
        toast.error(data.message || 'Failed to fetch reminders');
        setReminders([]);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error('Something went wrong. Please try again.');
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (updatedFormData) => {
    setFormData(updatedFormData);
    
    // Clear errors as user types
    const updatedErrors = { ...formErrors };
    Object.keys(updatedFormData).forEach(key => {
      if (updatedErrors[key]) {
        delete updatedErrors[key];
      }
    });
    
    setFormErrors(updatedErrors);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.scheduledDate) {
      errors.scheduledDate = 'Date and time are required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const data = await ReminderService.createReminder(formData, token);
      
      if (data.success) {
        toast.success('Reminder created successfully');
        setFormData({
          title: '',
          description: '',
          scheduledDate: new Date().toISOString().slice(0, 16),
          recurrence: 'none',
          isActive: true
        });
        setOpenAddDialog(false);
        fetchReminders();
      } else {
        toast.error(data.message || 'Failed to create reminder');
      }
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleComplete = async (reminderId, isCompleted) => {
    try {
      const response = await ReminderService.updateReminder(reminderId, { isCompleted: !isCompleted }, token);
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Reminder marked as ${!isCompleted ? 'completed' : 'incomplete'}`);
        fetchReminders();
      } else {
        toast.error(data.message || 'Failed to update reminder');
      }
    } catch (error) {
      console.error('Error updating reminder:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleDeleteClick = (reminder) => {
    setSelectedReminder(reminder);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReminder) return;
    
    try {
      setSubmitting(true);
      
      const data = await ReminderService.deleteReminder(selectedReminder._id, token);
      
      if (data.success) {
        toast.success('Reminder deleted successfully');
        setOpenDeleteDialog(false);
        setSelectedReminder(null);
        fetchReminders();
      } else {
        toast.error(data.message || 'Failed to delete reminder');
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRecurrenceText = (recurrence) => {
    switch (recurrence) {
      case 'daily':
        return 'Repeats daily';
      case 'weekly':
        return 'Repeats weekly';
      case 'monthly':
        return 'Repeats monthly';
      default:
        return 'One-time';
    }
  };

  const getRecurrenceColor = (recurrence) => {
    switch (recurrence) {
      case 'daily':
        return 'error';
      case 'weekly':
        return 'warning';
      case 'monthly':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading && reminders.length === 0) {
    return <Loading message="Loading reminders..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Workout Reminders"
        subtitle="Schedule and manage your workout reminders"
        breadcrumbs={[
          { name: 'Dashboard', path: '/dashboard' },
          { name: 'Reminders', path: '' }
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Add Reminder
          </Button>
        }
      />
      
      {/* Reminders List */}
      {reminders.length === 0 ? (
        <EmptyState
          title="No reminders found"
          description="Create your first reminder to help stay on track with your workout schedule."
          icon={<EventIcon sx={{ fontSize: 70 }} />}
          actionText="Add Reminder"
          onAction={() => setOpenAddDialog(true)}
        />
      ) : (
        <Grid container spacing={3}>
          {reminders.map(reminder => (
            <Grid item xs={12} sm={6} md={4} key={reminder._id}>
              <Card 
                elevation={1}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: reminder.isCompleted ? 0.8 : 1,
                  bgcolor: reminder.isCompleted ? 'action.hover' : 'background.paper'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ 
                      textDecoration: reminder.isCompleted ? 'line-through' : 'none',
                      color: reminder.isCompleted ? 'text.secondary' : 'text.primary'
                    }}>
                      {reminder.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {reminder.notificationStatus === 'sent' && (
                        <Chip
                          label="Notification Sent"
                          color="info"
                          size="small"
                        />
                      )}
                      <Chip
                        label={reminder.isCompleted ? 'Completed' : 'Active'}
                        color={reminder.isCompleted ? 'success' : 'primary'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(reminder.scheduledDate)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <RepeatIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Chip
                      label={getRecurrenceText(reminder.recurrence)}
                      color={getRecurrenceColor(reminder.recurrence)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  
                  {reminder.description && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
                      <DescriptionIcon fontSize="small" sx={{ mr: 1, mt: 0.3, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {reminder.description}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                
                <Divider />
                
                <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                  <Button
                    size="small"
                    startIcon={reminder.isCompleted ? <UncheckedIcon /> : <CheckCircleIcon />}
                    color={reminder.isCompleted ? 'inherit' : 'success'}
                    onClick={() => handleToggleComplete(reminder._id, reminder.isCompleted)}
                    disabled={reminder.hasNotificationSent}
                    title={reminder.hasNotificationSent ? "Cannot update reminders with sent notifications" : ""}
                  >
                    {reminder.isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
                  </Button>
                  
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleDeleteClick(reminder)}
                    disabled={reminder.hasNotificationSent}
                    title={reminder.hasNotificationSent ? "Cannot delete reminders with sent notifications" : ""}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Add Reminder Dialog */}
      <FormDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        title="Create New Reminder"
        onSubmit={handleSubmit}
        submitLabel="Create Reminder"
        loading={submitting}
      >
        <ReminderForm
          onChange={handleFormChange}
          errors={formErrors}
        />
      </FormDialog>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Reminder"
        message={`Are you sure you want to delete the reminder "${selectedReminder?.title}"?`}
        confirmLabel="Delete"
        type="delete"
        loading={submitting}
      />
      
      {/* Mobile Add Button */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16, display: { sm: 'none' } }}>
        <Fab color="primary" aria-label="add" onClick={() => setOpenAddDialog(true)}>
          <AddIcon />
        </Fab>
      </Box>
    </Box>
  );
};

export default Reminders;
